/**
 * ApiClient - HTTP клиент для API запросов
 *
 * Особенности:
 * - Автоматическая авторизация (Bearer token)
 * - Базовый URL
 * - Обработка ошибок
 * - Таймауты
 */

import type { ApiClient, RequestConfig } from '@/shell/types'

/**
 * Конфигурация API клиента
 */
export interface ApiClientConfig {
  /** Базовый URL API */
  baseUrl: string
  /** Функция получения токена */
  getToken?: () => string | null
  /** Таймаут по умолчанию (мс) */
  timeout?: number
  /** Заголовки по умолчанию */
  defaultHeaders?: Record<string, string>
}

/**
 * Ошибка API
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public response?: unknown
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/**
 * Реализация API клиента
 */
export class TLHApiClient implements ApiClient {
  private config: ApiClientConfig

  constructor(config: ApiClientConfig) {
    this.config = {
      timeout: 30000,
      defaultHeaders: {
        'Content-Type': 'application/json',
      },
      ...config,
    }
  }

  /**
   * GET запрос
   */
  async get<T = unknown>(url: string, config?: RequestConfig): Promise<T> {
    return this.request<T>('GET', url, undefined, config)
  }

  /**
   * POST запрос
   */
  async post<T = unknown>(url: string, data?: unknown, config?: RequestConfig): Promise<T> {
    return this.request<T>('POST', url, data, config)
  }

  /**
   * PUT запрос
   */
  async put<T = unknown>(url: string, data?: unknown, config?: RequestConfig): Promise<T> {
    return this.request<T>('PUT', url, data, config)
  }

  /**
   * DELETE запрос
   */
  async delete<T = unknown>(url: string, config?: RequestConfig): Promise<T> {
    return this.request<T>('DELETE', url, undefined, config)
  }

  /**
   * PATCH запрос
   */
  async patch<T = unknown>(url: string, data?: unknown, config?: RequestConfig): Promise<T> {
    return this.request<T>('PATCH', url, data, config)
  }

  /**
   * Обновить конфигурацию
   */
  updateConfig(config: Partial<ApiClientConfig>): void {
    this.config = { ...this.config, ...config }
  }

  /**
   * Установить функцию получения токена
   */
  setTokenGetter(getToken: () => string | null): void {
    this.config.getToken = getToken
  }

  // ============================================================================
  // Private
  // ============================================================================

  private async request<T>(
    method: string,
    url: string,
    data?: unknown,
    config?: RequestConfig
  ): Promise<T> {
    const fullUrl = this.buildUrl(url, config?.params)
    const headers = this.buildHeaders(config?.headers)
    const timeout = config?.timeout ?? this.config.timeout ?? 30000

    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), timeout)

    try {
      const response = await fetch(fullUrl, {
        method,
        headers,
        body: data ? JSON.stringify(data) : undefined,
        signal: controller.signal,
      })

      clearTimeout(timeoutId)

      if (!response.ok) {
        let errorBody: unknown
        try {
          errorBody = await response.json()
        } catch {
          errorBody = await response.text()
        }
        throw new ApiError(
          `API Error: ${response.status} ${response.statusText}`,
          response.status,
          errorBody
        )
      }

      // Пустой ответ
      if (response.status === 204) {
        return undefined as T
      }

      // Парсим JSON
      const contentType = response.headers.get('content-type')
      if (contentType?.includes('application/json')) {
        return response.json() as Promise<T>
      }

      // Текстовый ответ
      return (await response.text()) as T
    } catch (error) {
      clearTimeout(timeoutId)

      if (error instanceof ApiError) {
        throw error
      }

      if (error instanceof DOMException && error.name === 'AbortError') {
        throw new ApiError('Request timeout', 408)
      }

      throw new ApiError(error instanceof Error ? error.message : 'Network error', 0)
    }
  }

  private buildUrl(url: string, params?: Record<string, string | number | boolean>): string {
    // Абсолютный URL
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return this.appendParams(url, params)
    }

    // Относительный URL
    const baseUrl = this.config.baseUrl.replace(/\/$/, '')
    const path = url.startsWith('/') ? url : `/${url}`

    return this.appendParams(`${baseUrl}${path}`, params)
  }

  private appendParams(url: string, params?: Record<string, string | number | boolean>): string {
    if (!params || Object.keys(params).length === 0) {
      return url
    }

    const urlObj = new URL(url, window.location.origin)
    for (const [key, value] of Object.entries(params)) {
      urlObj.searchParams.set(key, String(value))
    }

    return urlObj.toString()
  }

  private buildHeaders(customHeaders?: Record<string, string>): Headers {
    const headers = new Headers(this.config.defaultHeaders)

    // Добавляем Authorization если есть токен
    const token = this.config.getToken?.()
    if (token) {
      headers.set('Authorization', `Bearer ${token}`)
    }

    // Добавляем кастомные заголовки
    if (customHeaders) {
      for (const [key, value] of Object.entries(customHeaders)) {
        headers.set(key, value)
      }
    }

    return headers
  }
}

// ============================================================================
// Factory
// ============================================================================

let apiClientInstance: TLHApiClient | null = null

/**
 * Получить singleton экземпляр ApiClient
 */
export function getApiClient(config?: Partial<ApiClientConfig>): TLHApiClient {
  if (!apiClientInstance) {
    apiClientInstance = new TLHApiClient({
      baseUrl: config?.baseUrl ?? '/api',
      ...config,
    })
  } else if (config) {
    apiClientInstance.updateConfig(config)
  }
  return apiClientInstance
}

/**
 * Создать новый экземпляр ApiClient
 */
export function createApiClient(config: ApiClientConfig): TLHApiClient {
  return new TLHApiClient(config)
}

export default TLHApiClient
